import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import axios from 'axios';
import i18n from './i18n';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import PropertyAnalysis from './pages/PropertyAnalysisImproved';
import DealView from './pages/DealView';
import Compare from './pages/Compare';
import Pricing from './pages/Pricing';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import RefundPolicy from './pages/RefundPolicy';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ScenarioAnalyzer from './pages/ScenarioAnalyzer';
import RentOptimizer from './pages/RentOptimizer';
import StressTest from './pages/StressTest';
import PortfolioDashboard from './pages/PortfolioDashboard';
import MyPortfolio from './pages/MyPortfolio';
import PortfolioCompare from './pages/PortfolioCompare';
import InvestorProfile from './pages/InvestorProfile';
import Account from './pages/Account';
import ListingImport from './pages/ListingImport';
import PortfolioDoctor from './pages/PortfolioDoctor';
import DealScanner from './pages/DealScanner';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import './App.css';

/** Route guard for the Deal Scanner: while the launch flag is OFF, any direct
 *  URL visit falls back to the Dashboard. We deliberately wait for the flag
 *  fetch to complete before deciding, so we don't briefly redirect internal
 *  testers who legitimately have the flag ON. */
function DealScannerGate() {
  const { flags, loading } = useFeatureFlags();
  if (loading) return null;
  if (!flags.deal_scanner_enabled) return <Navigate to="/dashboard" replace />;
  return <DealScanner />;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/analyze" element={<PropertyAnalysis />} />
      <Route path="/deal/:dealId" element={<DealView />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/scenario" element={<ScenarioAnalyzer />} />
      <Route path="/rent-optimizer/:dealId" element={<RentOptimizer />} />
      <Route path="/stress-test" element={<StressTest />} />
      <Route path="/portfolio" element={<MyPortfolio />} />
      <Route path="/portfolio/health" element={<PortfolioDashboard />} />
      <Route path="/portfolio/compare" element={<PortfolioCompare />} />
      <Route path="/profile" element={<InvestorProfile />} />
      <Route path="/account" element={<Account />} />
      <Route path="/listing-import" element={<ListingImport />} />
      <Route path="/portfolio-doctor" element={<PortfolioDoctor />} />
      <Route path="/deal-scanner" element={<DealScannerGate />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Sync saved language preference from backend (returning users keep their choice).
    // Silent on 401 for anonymous visitors.
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (apiBase) {
      axios
        .get(`${apiBase}/api/user/preferences`, { withCredentials: true })
        .then((res) => {
          const saved = res?.data?.language;
          if (saved && (saved === 'en' || saved === 'fr') && saved !== i18n.language) {
            i18n.changeLanguage(saved);
          }
        })
        .catch(() => { /* not authenticated, keep detected/default language */ });
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          // Don't show toast for auth check endpoints (those navigate on their own)
          // or best-effort background endpoints that safely 401 for anonymous users
          // (e.g. saving language preference from the pricing page).
          if (
            !url.includes('/auth/me') &&
            !url.includes('/auth/session') &&
            !url.includes('/user/preferences')
          ) {
            toast.error(i18n.t('errors.sessionExpired', 'Your session has expired. Please log in again.'));
            setTimeout(() => { window.location.href = '/'; }, 1500);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <FeatureFlagsProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </FeatureFlagsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
